import { useState, useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Skeleton } from "antd"
import type { AxiosResponse } from "axios"
import { Input } from "components/ui/inputs/Input"
import { useUpdateEref } from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { applyDisconnectedEcallsToCache, spliceErefIntoCache } from "helpers/quotationCache.helper"
import { useEntityMutationQueue } from "hooks/mutationQueue.hook"
import { erefQueueKey } from "components/ui/customWebviewer/ReferenceSidebar/erefQueueKey"
import type { TErefWithFurniture } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import style from "./NumberAndPage.module.scss"

const { wrapper, inputBlock, label, input } = style

type Props = { eref: TErefWithFurniture }

export const NumberAndPage = ({ eref }: Props) => {
	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutateAsync: updateErefAsync, isPending } = useUpdateEref()
	const { enqueue, versionRef } = useEntityMutationQueue(erefQueueKey(eref.id), eref.version)

	const savedElevationRef = useRef(eref.elevation_number ?? "")
	const savedPageNameRef = useRef(eref.page_name)
	const [elevationNumber, setElevationNumber] = useState(eref.elevation_number ?? "")
	const [pageName, setPageName] = useState(eref.page_name)

	useEffect(() => {
		savedElevationRef.current = eref.elevation_number ?? ""
		savedPageNameRef.current = eref.page_name
		setElevationNumber(eref.elevation_number ?? "")
		setPageName(eref.page_name)
	}, [eref.elevation_number, eref.page_name])

	const saveHandle = () => {
		if (elevationNumber === savedElevationRef.current && pageName === savedPageNameRef.current) return

		enqueue(async () => {
			try {
				const response = await updateErefAsync({
					quotationId,
					erefId: eref.id,
					body: {
						version: versionRef.current,
						...(elevationNumber !== savedElevationRef.current && { elevation_number: elevationNumber }),
						...(pageName !== savedPageNameRef.current && { page_name: pageName }),
					},
				})
				const updated = response?.data
				if (!updated) return
				const { disconnected_ecalls = [], disconnected_ecall_elevations = [], ...erefData } = updated
				versionRef.current = erefData.version
				savedElevationRef.current = erefData.elevation_number ?? ""
				savedPageNameRef.current = erefData.page_name
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					const next = spliceErefIntoCache(cached, erefData)
					return applyDisconnectedEcallsToCache(next, disconnected_ecalls, disconnected_ecall_elevations)
				})
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}

	return (
		<div className={wrapper}>
			<div className={inputBlock}>
				<span className={label}>Elevation number</span>
				{isPending ? (
					<Skeleton.Input active size="large" block />
				) : (
					<Input
						className={input}
						value={elevationNumber}
						onChange={(e) => setElevationNumber(e.target.value)}
						onBlur={saveHandle}
					/>
				)}
			</div>
			<div className={inputBlock}>
				<span className={label}>Page</span>
				{isPending ? (
					<Skeleton.Input active size="large" block />
				) : (
					<Input className={input} value={pageName} disabled />
				)}
			</div>
		</div>
	)
}
